import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { actionTypes } from 'Types';
import type { LearningMode, PlayerLayer, SettingContext } from './settingTypes';

/**
 * Layers that have no store in today's code yet: most settings' mode defaults
 * and instance values. They are kept here, in memory only (not redux-persisted,
 * cleared at sign-out), so the cascade works now; giving them a server store is
 * later work. Values are kept raw and read back through each setting's `parse`.
 */
export interface SettingsLayersState {
  global: Record<string, unknown>;
  mode: Partial<Record<LearningMode, Record<string, unknown>>>;
  /** Keyed by `<mode>:<instance id>`. */
  instance: Record<string, Record<string, unknown>>;
}

export const initialSettingsLayersState: SettingsLayersState = {
  global: {},
  mode: {},
  instance: {},
};

export interface SetSettingLayerValuePayload {
  key: string;
  layer: PlayerLayer;
  context: SettingContext;
  value: unknown;
}

export const instanceLayerKey = (mode: LearningMode, instanceId: string): string =>
  `${mode}:${instanceId}`;

const requireMode = (key: string, layer: PlayerLayer, context: SettingContext): LearningMode => {
  if (!context.mode) throw new Error(`A ${layer} value of ${key} needs a mode`);
  return context.mode;
};

const requireInstanceId = (key: string, context: SettingContext): string => {
  if (!context.instanceId) throw new Error(`An instance value of ${key} needs an instance id`);
  return context.instanceId;
};

const withLayerValue = (
  state: SettingsLayersState,
  { key, layer, context, value }: SetSettingLayerValuePayload,
): SettingsLayersState => {
  switch (layer) {
    case 'global':
      return { ...state, global: { ...state.global, [key]: value } };
    case 'mode': {
      const mode = requireMode(key, layer, context);
      return { ...state, mode: { ...state.mode, [mode]: { ...state.mode[mode], [key]: value } } };
    }
    case 'instance': {
      const id = instanceLayerKey(
        requireMode(key, layer, context),
        requireInstanceId(key, context),
      );
      return {
        ...state,
        instance: { ...state.instance, [id]: { ...state.instance[id], [key]: value } },
      };
    }
    default:
      throw new Error(`Unknown setting layer: ${String(layer)}`);
  }
};

const settingsLayersSlice = createSlice({
  name: 'settingsLayers',
  initialState: initialSettingsLayersState,
  reducers: {
    settingLayerValueSet(
      state,
      action: PayloadAction<SetSettingLayerValuePayload>,
    ): SettingsLayersState {
      return withLayerValue(state, action.payload);
    },
    settingsLayersCleared(): SettingsLayersState {
      return initialSettingsLayersState;
    },
  },
  extraReducers: (builder) => {
    // Sign-out clears the app's data; the next account starts with no kept layers.
    builder.addMatcher(
      (action): boolean => action.type === actionTypes.CLEAR_DATA,
      (): SettingsLayersState => initialSettingsLayersState,
    );
  },
});

export const { settingLayerValueSet, settingsLayersCleared } = settingsLayersSlice.actions;

export const settingsLayersReducer = settingsLayersSlice.reducer;

/** The raw in-memory value at one layer, or undefined when none is kept. */
export const selectSettingLayerValue = (
  state: { settingsLayersReducer?: SettingsLayersState },
  key: string,
  layer: PlayerLayer,
  context: SettingContext,
): unknown => {
  const layers = state.settingsLayersReducer;
  if (!layers) return undefined;
  switch (layer) {
    case 'global':
      return layers.global[key];
    case 'mode':
      return context.mode ? layers.mode[context.mode]?.[key] : undefined;
    case 'instance':
      return context.mode && context.instanceId
        ? layers.instance[instanceLayerKey(context.mode, context.instanceId)]?.[key]
        : undefined;
    default:
      return undefined;
  }
};
